import React, { useState } from "react";
import PropTypes from "prop-types";

const WineLabel = ({ drink, images = {}, className = "" }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Priority order for images: label first, then bottle, then fallback
  const rawImageUrl = images.label || images.bottle || drink.imageUrl;

  // Use the raw URL directly (Unsplash handles optimization)
  const imageUrl = rawImageUrl;

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = (error) => {
    setImageError(true);
    setIsLoading(false);
  };

  const fallbackContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        textAlign: "center",
        minHeight: "200px",
        width: "250px",
      }}
    >
      <span style={{ fontSize: "3rem", marginBottom: "1rem" }}>🍷</span>
      <h4
        style={{
          color: "var(--primary-navy)",
          marginBottom: "0.5rem",
          fontSize: "1rem",
        }}
      >
        {drink.name || "Wine"}
      </h4>
      <p style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
        {imageError
          ? "Image could not be loaded"
          : "Wine label image not available"}
      </p>
      {drink.producer && (
        <p
          style={{
            color: "var(--accent-gold)",
            fontSize: "0.7rem",
            marginTop: "0.5rem",
          }}
        >
          {drink.producer}
        </p>
      )}
    </div>
  );

  return (
    <div
      className={`detail-card-bg ${className}`}
      style={{ padding: "1.5rem", borderRadius: "0.5rem" }}
    >
      <h3
        className="detail-section-header"
        style={{
          fontSize: "1.25rem",
          marginBottom: "1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        🏷️ Wine Label
      </h3>

      <div
        style={{
          maxWidth: "300px",
          width: "fit-content",
          background:
            "linear-gradient(135deg, var(--warm-beige) 0%, #f0ead2 100%)",
          borderRadius: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          border: "1px solid var(--border-color)",
          margin: "0 auto",
        }}
      >
        {imageUrl && !imageError ? (
          <>
            {isLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "var(--warm-beige)",
                  zIndex: 1,
                }}
              >
                <div style={{ color: "var(--text-muted)" }}>
                  Loading image...
                </div>
              </div>
            )}
            <img
              src={imageUrl}
              alt={`${drink.name} wine label`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                maxHeight: "250px",
                maxWidth: "100%",
                height: "auto",
                width: "auto",
                objectFit: "contain",
                borderRadius: "0.25rem",
                opacity: isLoading ? 0 : 1,
                transition: "opacity 0.3s ease",
                display: "block",
              }}
            />
          </>
        ) : (
          fallbackContent
        )}
      </div>

      {images.source && !imageError && (
        <p
          style={{
            fontSize: "0.75rem",
            color: "var(--text-muted)",
            textAlign: "center",
            marginTop: "0.75rem",
          }}
        >
          Image courtesy of {images.attribution || images.source}
        </p>
      )}

      {imageUrl && !imageError && (
        <div
          style={{
            marginTop: "0.75rem",
            padding: "0.75rem",
            backgroundColor: "var(--background-light)",
            borderRadius: "0.375rem",
            border: "1px solid var(--border-color)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.875rem",
            }}
          >
            <span style={{ color: "var(--text-muted)" }}>Vintage:</span>
            <span style={{ fontWeight: "600", color: "var(--primary-navy)" }}>
              {drink.vintage || "N/A"}
            </span>
          </div>
          {drink.bin && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.875rem",
                marginTop: "0.25rem",
              }}
            >
              <span style={{ color: "var(--text-muted)" }}>Bin:</span>
              <span style={{ fontWeight: "600", color: "var(--accent-gold)" }}>
                #{drink.bin}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

WineLabel.propTypes = {
  drink: PropTypes.shape({
    name: PropTypes.string,
    imageUrl: PropTypes.string,
    producer: PropTypes.string,
    vintage: PropTypes.string,
    bin: PropTypes.string,
  }).isRequired,
  images: PropTypes.shape({
    label: PropTypes.string,
    bottle: PropTypes.string,
    source: PropTypes.string,
    attribution: PropTypes.string,
  }),
  className: PropTypes.string,
};

WineLabel.defaultProps = {
  images: {},
  className: "",
};

export default WineLabel;
